import { Stack, Grid } from "@mantine/core"
import { FormErrors, useForm, UseFormReturnType } from "@mantine/form"
import { Button, EqualWidthGrid, FormGroup as FormGroupUI } from "@tfg/ui"
import { useEffect, useState } from "react"

import { RenderField } from "./RenderField"

interface selectField {
  label: string
  value: string
  description?: string
}

export type formFieldType =
  | "text"
  | "switch"
  | "date"
  | "select"
  | "multiselect"
  | "countrySelect"
  | "languageSelect"
  | "tagsInput"
  | "selectListRegistrationId"
  | "reference"
  | "coordinates"

export interface FormFieldConfig {
  clearable?: boolean
  type: formFieldType
  name: string
  label: string
  placeholder: string
  customClass?: CSSModuleClasses
  options?: selectField[]
  tooltip?: string
  disabled?: boolean
  required?: boolean
  nameType?: string
  span?: number
  order?: number
  defaultChecked?: boolean
  tooltipJustify?: "start" | "end" | "center" | "space-between" | "space-around"
}

export interface FormRowConfig {
  type: "row"
  fields: FormFieldConfig[]
  spacing?: "xs" | "sm" | "md" | "lg" | "xl"
}

export interface FormGroupConfig {
  type: "group"
  title?: string
  description?: string
  fields: (FormFieldConfig | FormRowConfig)[]
  collapsible?: boolean
  defaultCollapsed?: boolean
  variant?: "default" | "filled" | "outline"
  spacing?: "xs" | "sm" | "md" | "lg" | "xl"
}

export type FormElement = FormFieldConfig | FormRowConfig | FormGroupConfig

export interface FormConfig<T> {
  validate: (values: Record<string, unknown>) => FormErrors
  initialValues: T
}

export interface TypesFormProps<T> {
  elements: FormElement[]
  submitLabel: string
  formConfig: FormConfig<T>
  mutate: (values: T) => void
  closeDrawer?: () => void
  cancelLabel?: string
  children?: React.ReactNode
}

const FormRow = <T extends Record<string, unknown>>({
  rowConfig,
  form,
}: {
  rowConfig: FormRowConfig
  form: UseFormReturnType<T>
}) => {
  return (
    <Grid gutter={rowConfig.spacing || "md"} w="100%">
      {rowConfig.fields.map((field, index) => (
        <Grid.Col
          key={index}
          span={field.span || Math.floor(12 / rowConfig.fields.length)}
        >
          <RenderField field={field} form={form} />
        </Grid.Col>
      ))}
    </Grid>
  )
}

const FormGroup = <T extends Record<string, unknown>>({
  groupConfig,
  form,
}: {
  groupConfig: FormGroupConfig
  form: UseFormReturnType<T>
}) => {
  const [collapsed, setCollapsed] = useState(
    groupConfig.defaultCollapsed || false
  )

  const handleToggle = () => {
    if (groupConfig.collapsible) {
      setCollapsed(!collapsed)
    }
  }

  const renderElement = (
    element: FormFieldConfig | FormRowConfig,
    index: number
  ) => {
    if (element.type === "row") {
      return <FormRow key={index} rowConfig={element} form={form} />
    } else {
      return (
        <Grid.Col key={index} span={element.span || 12}>
          <RenderField field={element} form={form} />
        </Grid.Col>
      )
    }
  }

  return (
    <FormGroupUI
      title={groupConfig.title}
      description={groupConfig.description}
      p="sm"
      w="100%"
      my="sm"
      style={{
        backgroundColor:
          groupConfig.variant === "filled"
            ? "var(--mantine-color-gray-0)"
            : "transparent",
      }}
      collapsible={groupConfig.collapsible}
      toggleCollapse={handleToggle}
      collapsed={collapsed}
    >
      {!collapsed && (
        <Stack gap={groupConfig.spacing || "md"} w="100%" p="xs">
          <Grid w="100%">{groupConfig.fields.map(renderElement)}</Grid>
        </Stack>
      )}
    </FormGroupUI>
  )
}

const RenderElement = <T extends Record<string, unknown>>({
  element,
  form,
}: {
  element: FormElement
  form: UseFormReturnType<T>
  index: number
}) => {
  switch (element.type) {
    case "row":
      return <FormRow rowConfig={element} form={form} />
    case "group":
      return <FormGroup groupConfig={element} form={form} />
    default:
      return (
        <Grid.Col span={element.span || 12}>
          <RenderField field={element} form={form} />
        </Grid.Col>
      )
  }
}

export const TypesForm = <T extends Record<string, unknown>>(
  props: TypesFormProps<T>
) => {
  const [formConfig, setFormConfig] = useState<FormConfig<T>>({
    ...props.formConfig,
  })
  useEffect(() => {
    setFormConfig(props.formConfig)
  }, [props.formConfig])

  const form = useForm({
    ...formConfig,
    validateInputOnChange: true,
  })

  const handleSubmit = () => {
    props.mutate(form.values)
    form.reset()
    if (props.closeDrawer) {
      props.closeDrawer()
    }
  }

  return (
    <>
      <form onSubmit={form.onSubmit(() => handleSubmit())}>
        <Stack h="100%" py="sm" gap="sm">
          <Grid px="xs">
            {props.elements.map((element, index) => (
              <RenderElement
                key={`${element.type}-${index}`}
                element={element}
                form={form}
                index={index}
              />
            ))}
          </Grid>
          {props.children}
        </Stack>
        <Stack py="md" pos="sticky" bg="white" bottom={0} left={0} right={0}>
          <EqualWidthGrid>
            <Button
              size="sm"
              variant="tertiary"
              onClick={props.closeDrawer}
              fullWidth
            >
              {props.cancelLabel}
            </Button>
            <Button
              size="sm"
              disabled={!form.isValid()}
              type="submit"
              fullWidth
            >
              {props.submitLabel}
            </Button>
          </EqualWidthGrid>
        </Stack>
      </form>
    </>
  )
}
